/**
 * Loader for the `almanak.best_time` table ("best time to visit {country}" pages).
 *
 * Moves the former hardcoded `BEST_TIME_PAGES` array out of best-time-pages.ts
 * into a DB table so pages can be admin-edited and pipeline-enriched. Follows the
 * preload → sync-getter pattern used by db-supabase.ts: server entry points must
 * `await preloadBestTime()` before any sync reader (getBestTimeRows) runs. Backed
 * by unstable_cache cross-request; a Promise singleton dedupes concurrent callers.
 *
 * The table is not in the generated database.types.ts (admin-managed, regenerated
 * manually), so rows are fetched untyped and shaped here. meta_title /
 * meta_description carry {year} / {site} placeholders resolved by the caller
 * (best-time-pages.ts) against CALENDAR_YEAR / SITE_NAME.
 *
 * Refs: almanak-pju1, almanak-pju1.2
 */
import { createClient } from '@supabase/supabase-js';
import { unstable_cache } from 'next/cache';
import { fetchAllUntyped } from './db-helpers';
import type { Database } from './database.types';

/** Raw shape of an almanak.best_time row (snake_case, jsonb columns typed loosely). */
export interface BestTimeRow {
  slug: string;
  display_name: string;
  country_code: string;
  meta_title: string;
  meta_description: string;
  seasons: unknown;
  months: unknown;
  best_months: number[];
  faq: unknown;
  sort_order: number;
  status: string;
}

const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
if (!url || !key) {
  throw new Error('Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY');
}
const sb = createClient<Database, 'almanak'>(url, key, { db: { schema: 'almanak' } });

const _cachedBestTimeRaw = unstable_cache(
  async () => fetchAllUntyped(sb, 'best_time'),
  ['db-best-time:best_time'],
  { revalidate: 3600, tags: ['best_time'] },
);

let _bestTime: BestTimeRow[] | null = null;
let _preloadPromise: Promise<void> | null = null;

async function _doPreload(): Promise<void> {
  const rows = await _cachedBestTimeRaw();
  _bestTime = (rows as unknown as BestTimeRow[])
    .filter(r => r.status === 'published')
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** Preload best-time pages. Idempotent Promise singleton. */
export function preloadBestTime(): Promise<void> {
  if (!_preloadPromise) {
    _preloadPromise = _doPreload().catch(err => {
      _preloadPromise = null;
      throw err;
    });
  }
  return _preloadPromise;
}

/** True once the best-time cache is populated (gates the evergreen registry). */
export function isBestTimeLoaded(): boolean {
  return _bestTime !== null;
}

/** Published best-time rows, sort_order ascending. Empty until preloaded. */
export function getBestTimeRows(): BestTimeRow[] {
  return _bestTime ?? [];
}

/** Test-only: clear the preload singleton so a fresh preloadBestTime() re-fetches. */
export function _resetPreloadBestTimeForTesting(): void {
  _preloadPromise = null;
  _bestTime = null;
}
